import { useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import { appAPI } from '../../utils/api';
import urls from '../../utils/urls';
import { useMemo } from 'react';
import { getOrganizationEntityAuditLogsQueryCacheKey } from './cacheKeys';
import { useOrganizationId } from './useOrganization';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { getIntl } from '../../utils/intl';

const intl = getIntl('UseOrganizationEntityAuditLogs', {
    auditLogsFailure: 'Failed to load entity audit logs',
});

export const useOrganizationEntityAuditLogs = () => {
    const organizationId = useOrganizationId();
    const organizationAuditLogsQuery = useQuery({
        queryKey: getOrganizationEntityAuditLogsQueryCacheKey(),
        queryFn: async () => {
            return await appAPI.get<APITypes.organizationService.ListOrganizationEntityAuditLogsResponse>(
                urls.api.organizations.auditLogs(organizationId),
            );
        },
        throwOnError: true,
    });
    useQueryErrorNotify(organizationAuditLogsQuery, intl('auditLogsFailure'));

    const isLoading = organizationAuditLogsQuery.isLoading;
    const auditLogs = organizationAuditLogsQuery.data?.items;
    const sortedAuditLogs = useMemo(() => {
        if (isLoading) return [];
        return auditLogs?.sort((a, b) => b.timestamp - a.timestamp);
    }, [auditLogs, isLoading]);

    return {
        auditLogs: sortedAuditLogs,
        isLoading,
    };
};
